<script setup lang="ts">
/**
 * A chapter's image block. `asset` is read defensively (`block.asset?.url`) — stored `data` is
 * unvalidated. `asset.url` holds an object path or an external URL, never a signed URL.
 */
import type { InputPrimaryEvent } from '@shared/ui/fields'
import type { ImageBlock } from '../../../model/assessments/interfaces'
import type { AssignmentContentOwner } from '../../../model/assignments/store/interfaces'

import { InputPrimary } from '@shared/ui/fields'

import { useAssignmentContentBlockImage } from '../../../model/assignments/content-block-editors.hook'
import AssignmentImageUploader from './AssignmentImageUploader.vue'

defineOptions({
  name: 'ChapterBlockImage',
})

const props = defineProps<{
  owner: AssignmentContentOwner
  index: number
  block: ImageBlock
}>()

const {
  setUrl,
  setAlt,
  bindSetUrl,
} = useAssignmentContentBlockImage({
  owner: () => props.owner,
  index: () => props.index,
  block: () => props.block,
})
</script>

<template>
  <div class="chapter-block-image">
    <!-- `bind-change` pins an upload to THIS block when it starts; `update:value` (paste, remove)
         is synchronous and can use the live index. -->
    <AssignmentImageUploader
      :value="block.asset?.url"
      :alt="block.alt"
      :bind-change="bindSetUrl"
      @update:value="setUrl"
    >
      <template #fields>
        <!-- Optional: a decorative illustration needs no description. The block always carries an
             `alt` string (`''` when left blank), so the shared `ImageBlock` shape holds. -->
        <InputPrimary
          name="alt"
          label="Alt text"
          optional-label="optional"
          :value="block.alt"
          placeholder="What the image shows"
          @input="(event: InputPrimaryEvent) => setAlt(String(event.value))"
        />
      </template>
    </AssignmentImageUploader>
  </div>
</template>

<style scoped lang="scss" src="./ChapterBlockImage.scss" />
